import { DEFAULT_PROFILE_LANGUAGE, normalizeProfileLanguage, type ProfileLanguage } from "@timeghost/i18n/languages";
import { parseUsername } from "@/lib/users/parse-username";

export interface UserApiToken {
	readonly id: string;
	readonly name: string;
	readonly position: number;
	readonly token: string;
}

export const isMaskedApiToken = (token: string): boolean => token.includes("*");

const API_TOKEN_PREFIX = "tg.";
const LEGACY_API_TOKEN_HEAD = 4;
const PREFIXED_API_TOKEN_SECRET_HEAD = 8;
const API_TOKEN_PREVIEW_TAIL = 4;

const apiTokenPreviewHead = (token: string): number =>
	token.startsWith(API_TOKEN_PREFIX) ? API_TOKEN_PREFIX.length + PREFIXED_API_TOKEN_SECRET_HEAD : LEGACY_API_TOKEN_HEAD;

export const maskApiTokenSecret = (token: string): string =>
	isMaskedApiToken(token) ? token : `${token.slice(0, apiTokenPreviewHead(token))}********************************`;

const asRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object") {
		return null;
	}
	return value as Record<string, unknown>;
};

const asString = (value: unknown, fallback = ""): string => (typeof value === "string" ? value : fallback);

export const remaskUserApiTokens = <T>(user: T): T => {
	if (!user || typeof user !== "object") {
		return user;
	}
	const raw = user as Record<string, unknown>;
	if (!Array.isArray(raw.apiTokens)) {
		return user;
	}
	return {
		...raw,
		apiTokens: raw.apiTokens.map((item) => {
			const token = asRecord(item);
			if (!token || typeof token.token !== "string") {
				return item;
			}
			return { ...token, token: maskApiTokenSecret(token.token) };
		}),
	} as T;
};

export const apiTokenPreview = (token: string): string => {
	if (isMaskedApiToken(token)) {
		return token;
	}
	const head = apiTokenPreviewHead(token);
	if (token.length <= head + API_TOKEN_PREVIEW_TAIL + 3) {
		return token;
	}
	return `${token.slice(0, head)}...${token.slice(-API_TOKEN_PREVIEW_TAIL)}`;
};

export const PROFILE_WIDGET_PREFERENCES = ["sameWidget", "noWidget"] as const;

export type ProfileWidgetPreference = (typeof PROFILE_WIDGET_PREFERENCES)[number];

export const isProfileWidgetPreference = (value: string): value is ProfileWidgetPreference =>
	(PROFILE_WIDGET_PREFERENCES as readonly string[]).includes(value);

export interface ProfileSettings {
	readonly dateFormat: string;
	readonly languageSetting: ProfileLanguage;
	readonly timeFormat24h: boolean;
	readonly timeZone: string;
	readonly widgetPreference: ProfileWidgetPreference;
}

export const PROFILE_DATE_FORMATS = ["dd/MM/yyyy", "MM/dd/yyyy", "yyyy-MM-dd", "dd.MM.yyyy"] as const;

export const USER_API_TOKEN_NAME = "api-token";

export const browserTimeZone = (): string => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
	} catch {
		return "UTC";
	}
};

export const listTimeZones = (): readonly string[] => {
	const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
		"timeZone"
	);
	if (Array.isArray(supported) && supported.length > 0) {
		return supported;
	}
	const fallback = ["UTC", "Europe/Berlin", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Tokyo"];
	const current = browserTimeZone();
	return fallback.includes(current) ? fallback : [current, ...fallback];
};

export const parseProfileSettings = (rawUser: unknown): ProfileSettings => {
	const raw = asRecord(rawUser);
	const settings = asRecord(raw?.settings) ?? {};
	const language = asString(settings.languageSetting, DEFAULT_PROFILE_LANGUAGE);
	const widgetPreference = asString(settings.widgetPreference, "sameWidget");
	return {
		timeZone: asString(settings.timeZone, browserTimeZone()),
		languageSetting: normalizeProfileLanguage(language),
		timeFormat24h: settings.timeFormat24h !== false,
		dateFormat: asString(settings.dateFormat, "dd/MM/yyyy") || "dd/MM/yyyy",
		widgetPreference: isProfileWidgetPreference(widgetPreference) ? widgetPreference : "sameWidget",
	};
};

export const parseApiTokens = (rawUser: unknown): readonly UserApiToken[] => {
	const raw = asRecord(rawUser);
	if (!Array.isArray(raw?.apiTokens)) {
		return [];
	}
	return raw.apiTokens
		.map((item, index) => {
			const token = asRecord(item);
			if (!token) {
				return null;
			}
			const value = asString(token.token).trim();
			if (!value) {
				return null;
			}
			const name = asString(token.name).trim() || USER_API_TOKEN_NAME;
			const position = typeof token.position === "number" && Number.isFinite(token.position) ? token.position : index;
			const id = asString(token.id).trim() || `pos:${position}`;
			return { id, name, position, token: value };
		})
		.filter((item): item is UserApiToken => item !== null);
};

export const nextApiTokenPosition = (tokens: readonly UserApiToken[]): number =>
	tokens.reduce((max, token) => Math.max(max, token.position), -1) + 1;

/** Client placeholder; API encrypts this into a secret. */
export const buildApiTokenValue = (): string => "CREATE_TOKEN_PLACEHOLDER";

export const displayNameFromUser = (rawUser: unknown): string => {
	const raw = asRecord(rawUser);
	if (!raw) {
		return "";
	}
	const office = asRecord(raw.officeProfile);
	return parseUsername(
		asString(office?.displayName).trim() ||
			asString(raw.name).trim() ||
			asString(raw.email).trim() ||
			asString(raw.mail).trim()
	);
};

export const emailFromUser = (rawUser: unknown): string => {
	const raw = asRecord(rawUser);
	if (!raw) {
		return "";
	}
	const office = asRecord(raw.officeProfile);
	return asString(office?.mail).trim() || asString(raw.email).trim() || asString(raw.mail).trim();
};
